/**
 * Search drawer tabs — mouse drag-to-scroll with iOS-like momentum.
 *
 * Why this exists:
 *   The predictive-search tab list (PRODUCTS / SUGGESTIONS / COLLECTIONS / ...)
 *   is wider than the search drawer when there are 3+ tab categories. The
 *   container uses native `overflow: auto`, which works for trackpad two-finger
 *   swipe, touch swipe, Shift+wheel, and keyboard nav — but a regular mouse
 *   click+drag selects text instead of scrolling. Users without a trackpad
 *   could not reach the trailing tabs. This script enables click+drag with
 *   inertial release so every input device can navigate the tabs.
 *
 * What it does:
 *   - Tracks mousedown on the tab list, then mousemove on `document` (so drags
 *     keep working even when the cursor leaves the tabs).
 *   - Drag below the threshold is treated as a click (tab switches normally).
 *   - Drag above the threshold scrolls the list 1:1 with the cursor and
 *     suppresses the trailing click so the wrong tab does not activate.
 *   - On release, computes velocity from the last ~100ms of motion and
 *     animates a friction-based momentum scroll (cancelled if the user
 *     mousedowns or wheels again, or hits an edge).
 *
 * Scope:
 *   Only attaches to `<search-drawer>` instances that are NOT full-width
 *   (the full-width mode lays the tabs out as a column grid, no scroll).
 *
 * Coupling note:
 *   The scrollable element lives inside `<x-tabs>`'s shadow DOM (the slot
 *   with `role="tablist"`). We reach into `shadowRoot` to find it. If the
 *   x-tabs component template changes (slot role, part name, etc.), this
 *   binding will silently no-op — see `bindSearchDrawer()`.
 */
(() => {
  const CONFIG = {
    dragThreshold: 8,
    velocityWindowMs: 100,
    minReleaseVelocity: 0.15,
    maxVelocity: 4,
    friction: 0.97,
    minMomentumVelocity: 0.04,
  };

  class MomentumDragScroll {
    constructor(scrollEl, config = CONFIG) {
      this.el = scrollEl;
      this.cfg = config;

      this.active = false;
      this.dragged = false;
      this.startX = 0;
      this.startScroll = 0;
      this.samples = [];
      this.rafId = null;

      this._onDown = this._onDown.bind(this);
      this._onMove = this._onMove.bind(this);
      this._onUp = this._onUp.bind(this);
      this._onClick = this._onClick.bind(this);
      this._cancelMomentum = this._cancelMomentum.bind(this);

      this.el.addEventListener('mousedown', this._onDown);
      this.el.addEventListener('click', this._onClick, { capture: true });
      this.el.addEventListener('wheel', this._cancelMomentum, { passive: true });
      document.addEventListener('mousemove', this._onMove);
      document.addEventListener('mouseup', this._onUp);
    }

    destroy() {
      this._cancelMomentum();
      this.el.removeEventListener('mousedown', this._onDown);
      this.el.removeEventListener('click', this._onClick, { capture: true });
      this.el.removeEventListener('wheel', this._cancelMomentum);
      document.removeEventListener('mousemove', this._onMove);
      document.removeEventListener('mouseup', this._onUp);
    }

    _onDown(e) {
      if (e.button !== 0) return;
      this._cancelMomentum();
      this.active = true;
      this.dragged = false;
      this.startX = e.clientX;
      this.startScroll = this.el.scrollLeft;
      this.samples = [{ x: e.clientX, t: performance.now() }];
    }

    _onMove(e) {
      if (!this.active) return;
      const walk = e.clientX - this.startX;
      if (!this.dragged && Math.abs(walk) > this.cfg.dragThreshold) {
        this.dragged = true;
        this.el.style.userSelect = 'none';
      }
      if (!this.dragged) return;
      e.preventDefault();
      this.el.scrollLeft = this.startScroll - walk;
      this._recordSample(e.clientX);
    }

    _onUp() {
      if (!this.active) return;
      this.active = false;
      this.el.style.userSelect = '';
      if (this.dragged) this._startMomentum();
    }

    _onClick(e) {
      if (!this.dragged) return;
      e.stopPropagation();
      e.preventDefault();
      this.dragged = false;
    }

    _recordSample(x) {
      const t = performance.now();
      this.samples.push({ x, t });
      const cutoff = t - this.cfg.velocityWindowMs;
      while (this.samples.length > 2 && this.samples[0].t < cutoff) {
        this.samples.shift();
      }
    }

    _releaseVelocity() {
      if (this.samples.length < 2) return 0;
      const first = this.samples[0];
      const last = this.samples[this.samples.length - 1];
      const dt = last.t - first.t;
      return dt > 0 ? (last.x - first.x) / dt : 0;
    }

    _startMomentum() {
      let v = this._releaseVelocity();
      if (Math.abs(v) < this.cfg.minReleaseVelocity) return;
      v = Math.max(-this.cfg.maxVelocity, Math.min(this.cfg.maxVelocity, v));

      let lastFrame = performance.now();
      const max = this.el.scrollWidth - this.el.clientWidth;
      const tick = (now) => {
        const dt = now - lastFrame;
        lastFrame = now;
        this.el.scrollLeft -= v * dt;
        v *= this.cfg.friction ** (dt / 16);

        const atEdge = this.el.scrollLeft <= 0 || this.el.scrollLeft >= max;
        if (!atEdge && Math.abs(v) > this.cfg.minMomentumVelocity) {
          this.rafId = requestAnimationFrame(tick);
        } else {
          this.rafId = null;
        }
      };
      this.rafId = requestAnimationFrame(tick);
    }

    _cancelMomentum() {
      if (this.rafId !== null) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
    }
  }

  // The scrollable element lives inside <x-tabs>'s shadow DOM (the slot with
  // role="tablist"). It's only there once predictive-search has rendered
  // results, and gets replaced on every keystroke — so we observe and rebind.
  function bindSearchDrawer(drawer) {
    const predictive = drawer.querySelector('predictive-search');
    if (!predictive) return;

    let current = null;

    const wireUp = () => {
      const xTabs = drawer.querySelector('.predictive-search__tabs');
      const tabList = xTabs?.shadowRoot?.querySelector('[role="tablist"]');

      if (current && current.el !== tabList) {
        current.destroy();
        current = null;
      }
      if (tabList && !current) {
        current = new MomentumDragScroll(tabList);
      }
    };

    wireUp();
    new MutationObserver(wireUp).observe(predictive, { childList: true, subtree: true });
  }

  function init() {
    document.querySelectorAll('search-drawer:not(.search-drawer--full)').forEach(bindSearchDrawer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
